import { useEffect, useRef } from "react"

// Hover "colour bleed" for a black and white image.
//
// Attach `ref` and `handlers` to the element that wraps both the greyscale
// layer and the colour layer. On hover, a soft circle of colour grows out from
// where the cursor entered and drifts after it; on leave it shrinks away.
// The circle is driven by the CSS variables --x, --y and --r on that element.
export default function useHoverBloom({ onOpen, onClosed } = {}) {
  const ref = useRef(null)
  const st = useRef({ x: 50, y: 50, tx: 50, ty: 50, r: 0, tr: 0, raf: null })

  const paint = () => {
    const el = ref.current
    if (!el) return
    const { x, y, r } = st.current
    el.style.setProperty("--x", `${x}%`)
    el.style.setProperty("--y", `${y}%`)
    el.style.setProperty("--r", `${Math.max(r, 0.01)}px`)
  }

  const tick = () => {
    const s = st.current
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    s.r += (s.tr - s.r) * (reduce ? 1 : 0.07)
    s.x += (s.tx - s.x) * (reduce ? 1 : 0.12)
    s.y += (s.ty - s.y) * (reduce ? 1 : 0.12)
    paint()
    const moving =
      Math.abs(s.tr - s.r) > 0.5 ||
      Math.abs(s.tx - s.x) > 0.1 ||
      Math.abs(s.ty - s.y) > 0.1
    s.raf = moving ? requestAnimationFrame(tick) : null
    if (!moving && s.tr === 0 && onClosed) onClosed()
  }

  const run = () => {
    if (!st.current.raf) st.current.raf = requestAnimationFrame(tick)
  }

  const aim = e => {
    const rect = ref.current.getBoundingClientRect()
    st.current.tx = ((e.clientX - rect.left) / rect.width) * 100
    st.current.ty = ((e.clientY - rect.top) / rect.height) * 100
  }

  useEffect(() => {
    const s = st.current
    return () => cancelAnimationFrame(s.raf)
  }, [])

  const handlers = {
    onPointerEnter: e => {
      const rect = ref.current.getBoundingClientRect()
      aim(e)
      st.current.x = st.current.tx
      st.current.y = st.current.ty
      st.current.tr = Math.hypot(rect.width, rect.height) * 1.1
      if (onOpen) onOpen()
      run()
    },
    onPointerMove: e => {
      aim(e)
      run()
    },
    onPointerLeave: () => {
      st.current.tr = 0
      run()
    },
  }

  return { ref, handlers }
}
